import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import {
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import { createZenTheme } from './zenTheme';
import {
  DEFAULT_THEME_PALETTE,
  THEME_PALETTES,
  resolvePalette,
} from './pdvPalettes';
import { updateMeuTema } from '../services/api';
import { getAuthToken } from '../services/authStorage';

/// Preferências preservadas pelo `clearBrowserCache` da tela de login.
export const THEME_MODE_KEY = 'zen.themeMode';
export const THEME_PALETTE_KEY = 'zen.themePalette';

const ThemeModeContext = createContext({
  mode: 'light',
  palette: DEFAULT_THEME_PALETTE,
  toggleMode: () => {},
  setMode: () => {},
  setPalette: () => {},
  applyPreferences: () => {},
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

function readInitialMode() {
  try {
    const saved = localStorage.getItem(THEME_MODE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    // ignore
  }
  return 'light';
}

function readInitialPalette() {
  try {
    const saved = localStorage.getItem(THEME_PALETTE_KEY);
    if (saved && resolvePalette(saved)?.value === saved) return saved;
  } catch {
    // ignore
  }
  return DEFAULT_THEME_PALETTE;
}

function persistLocal(mode, palette) {
  try {
    localStorage.setItem(THEME_MODE_KEY, mode);
    localStorage.setItem(THEME_PALETTE_KEY, palette);
  } catch {
    // preferência só nesta sessão
  }
}

export default function ThemeModeProvider({ children }) {
  const [mode, setModeState] = useState(readInitialMode);
  const [palette, setPaletteState] = useState(readInitialPalette);
  const saveTimer = useRef(null);

  const persistRemote = useCallback((nextMode, nextPalette) => {
    const token = getAuthToken();
    if (!token) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      updateMeuTema({
        tema: nextPalette,
        temaEscuro: nextMode === 'dark',
      }).catch(() => {
        // falha silenciosa — localStorage já guarda
      });
    }, 350);
  }, []);

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const applyPreferences = useCallback((prefs) => {
    if (!prefs) return;
    let nextMode = null;
    if (prefs.temaEscuro === true || prefs.mode === 'dark') nextMode = 'dark';
    else if (prefs.temaEscuro === false || prefs.mode === 'light') nextMode = 'light';

    const nextPaletteRaw = prefs.tema || prefs.palette;
    const nextPalette =
      nextPaletteRaw && resolvePalette(nextPaletteRaw)
        ? resolvePalette(nextPaletteRaw).value
        : null;

    setModeState((currentMode) => {
      const modeToUse = nextMode || currentMode;
      setPaletteState((currentPal) => {
        const paletteToUse = nextPalette || currentPal;
        persistLocal(modeToUse, paletteToUse);
        return paletteToUse;
      });
      return modeToUse;
    });
  }, []);

  const setMode = useCallback(
    (next) => {
      setModeState((current) => {
        const value = typeof next === 'function' ? next(current) : next;
        const modeToUse = value === 'dark' ? 'dark' : 'light';
        persistLocal(modeToUse, palette);
        persistRemote(modeToUse, palette);
        return modeToUse;
      });
    },
    [palette, persistRemote],
  );

  const toggleMode = useCallback(() => {
    setMode((current) => (current === 'dark' ? 'light' : 'dark'));
  }, [setMode]);

  const setPalette = useCallback(
    (name) => {
      const resolved = resolvePalette(name)?.value || DEFAULT_THEME_PALETTE;
      setPaletteState(resolved);
      persistLocal(mode, resolved);
      persistRemote(mode, resolved);
    },
    [mode, persistRemote],
  );

  const theme = useMemo(() => createZenTheme(mode, palette), [mode, palette]);
  const value = useMemo(
    () => ({
      mode,
      palette,
      toggleMode,
      setMode,
      setPalette,
      applyPreferences,
    }),
    [mode, palette, toggleMode, setMode, setPalette, applyPreferences],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

/// Botão sol/lua + seletor de cores na barra.
export function ThemeModeToggle({ sx, ...props }) {
  const { mode, palette, toggleMode, setPalette } = useThemeMode();
  const isDark = mode === 'dark';
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const current = resolvePalette(palette);

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={sx}>
      <Tooltip title="Cores do tema">
        <IconButton
          color="inherit"
          onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}
          {...props}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${current.primary} 50%, ${current.secondary} 50%)`,
              border: '2px solid rgba(255,255,255,0.65)',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
            }}
          />
        </IconButton>
      </Tooltip>
      <Tooltip title={isDark ? 'Modo claro' : 'Modo escuro'}>
        <IconButton onClick={toggleMode} color="inherit" {...props}>
          {isDark ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchor}
        placement="bottom-end"
        style={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={() => setAnchor(null)}>
          <Paper elevation={6} sx={{ mt: 1, p: 1.5, width: 260 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <PaletteOutlinedIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700}>
                Tema
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 1,
              }}
            >
              {THEME_PALETTES.map((opt) => {
                const selected = opt.value === current.value;
                return (
                  <Tooltip key={opt.value} title={opt.value}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => {
                        setPalette(opt.value);
                        setAnchor(null);
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: selected
                          ? '2px solid'
                          : '1px solid rgba(0,0,0,0.12)',
                        borderColor: selected ? 'primary.main' : undefined,
                        p: 0.35,
                        cursor: 'pointer',
                        background: 'transparent',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${opt.primary} 50%, ${opt.secondary} 50%)`,
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
            <Divider sx={{ my: 1.25 }} />
            <Typography variant="caption" color="text.secondary">
              {current.value} · {isDark ? 'escuro' : 'claro'} — salvo na sua conta
            </Typography>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
}
